import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Flag, Handshake, Linkedin, Award, Gauge, ShieldCheck, FileText, Cpu, BadgeCheck } from "lucide-react";
import PageHeader from "@/components/common/PageHeader";
import { Reveal } from "@/components/common/Reveal";
import Counter from "@/components/common/Counter";
import { COMPANY, STATS, VALUES, PROCESS, LEADERSHIP, PRIMARY_CERTIFICATION, UDYAM_REGISTRATION, ACCREDITATIONS } from "@/data/content";

const PILLARS = [
  { icon: Target, title: "Our Mission", text: "To deliver reliable, precise, and cost-effective machine tool solutions through engineering expertise, advanced CNC automation, quality reconditioning, and customer-focused service." },
  { icon: Eye, title: "Our Vision", text: "To become a trusted and recognized name in the machine tool industry, known for engineering excellence, dependable technology, quality manufacturing, and long-term customer partnerships." },
  { icon: Flag, title: "Our Goal", text: "To continuously expand our capabilities — from CNC retrofitting and machine reconditioning to the design and production of VTL and Turning Center machines — delivering machines built for precision, productivity, and industrial performance." },
  { icon: Handshake, title: "Our Commitment", text: "We combine 20+ years of machine tool experience with modern CNC technology and strong engineering practices to provide dependable solutions that help our customers improve productivity, reduce downtime, and achieve long-term value." },
];

const About = () => (
  <div>
    {/* Compact page hero with logo */}
    <section className="relative pt-32 pb-10 md:pt-36 md:pb-12 bg-white border-b border-slate-200">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid md:grid-cols-[auto_1fr] gap-8 items-center">
        <img src={COMPANY.logo} alt="Accurate Logo" className="h-20 md:h-24 w-auto rounded-md" data-testid="about-logo" />
        <div>
          <span className="eyebrow">Company Profile</span>
          <h1 className="mt-3 text-3xl md:text-4xl text-slate-900">Engineering expertise in CNC retrofitting, machine reconditioning, and industrial automation.</h1>
          <p className="mt-3 text-dim max-w-2xl text-sm md:text-base">Since {COMPANY.founded}, {COMPANY.legal} has been a trusted partner behind the machines that power modern manufacturing across India.</p>
        </div>
      </div>
    </section>

    {/* Story */}
    <section className="py-8 md:py-12">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-8">
        <Reveal className="lg:col-span-4">
          <span className="eyebrow">Our Story</span>
          <h2 className="mt-3 text-2xl md:text-3xl leading-tight text-slate-900">Craft meets cutting-edge control</h2>
        </Reveal>
        <div className="lg:col-span-8 space-y-4 text-dim text-base leading-relaxed">
          <Reveal><p>What began as a small machine-rebuild workshop has grown into a full-service engineering company trusted by leading manufacturers across India. Our philosophy is simple: honour the mechanical craft of the machine tool, and elevate it with the world's best control technology.</p></Reveal>
          <Reveal delay={0.1}><p>Today, our certified engineers deliver end-to-end CNC retrofitting, reconditioning, automation and lifetime support — with FANUC and Siemens systems at the core of everything we build.</p></Reveal>
        </div>
      </div>
    </section>

    {/* Pillars */}
    <section className="py-8 md:py-12 border-t border-slate-200 bg-slate-50">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="mb-8">
          <span className="eyebrow">What Drives Us</span>
          <h2 className="mt-3 text-2xl md:text-3xl text-slate-900">Mission, Vision &amp; Commitment</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {PILLARS.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.05} className="bg-white border border-[#0055FF]/30 rounded-2xl p-6 md:p-7 card-hover">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#0055FF]/10 border border-[#0055FF]/25 flex items-center justify-center"><b.icon className="w-5 h-5 text-[#0055FF]" /></div>
                <h3 className="text-lg text-slate-900">{b.title}</h3>
              </div>
              <p className="mt-3 text-dim leading-relaxed text-sm">{b.text}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* Values */}
    <section className="py-8 md:py-12">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <span className="eyebrow">Core Values</span>
        <h2 className="mt-3 text-2xl md:text-3xl mb-8 text-slate-900">What we stand for</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.06} className="border border-[#0055FF]/30 bg-white rounded-2xl p-6 card-hover">
              <span className="font-display text-4xl text-[#0055FF]/25">0{i + 1}</span>
              <h3 className="mt-2 text-base text-slate-900">{v.title}</h3>
              <p className="mt-2 text-dim text-sm leading-relaxed">{v.desc}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* Process */}
    <section className="py-8 md:py-12 border-t border-slate-200 bg-slate-50">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <span className="eyebrow">Our Process</span>
        <h2 className="mt-3 text-2xl md:text-3xl mb-10 text-slate-900">From assessment to lifetime support</h2>
        <div className="relative">
          <div className="absolute left-0 right-0 top-7 h-px bg-slate-200 hidden md:block" />
          <div className="grid md:grid-cols-5 gap-5">
            {PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.06} className="relative">
                <div className="w-14 h-14 rounded-full bg-white border border-[#0055FF]/40 flex items-center justify-center font-display text-lg text-[#0055FF] relative z-10 shadow-sm">{p.step}</div>
                <h3 className="mt-4 text-base text-slate-900">{p.title}</h3>
                <p className="mt-1 text-dim text-sm leading-relaxed">{p.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* Leadership — text only, no portraits */}
    <section className="py-8 md:py-12 border-t border-slate-200 bg-white" data-testid="leadership-section">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="mb-8">
          <span className="eyebrow">Meet the Team</span>
          <h2 className="mt-3 text-2xl md:text-3xl text-slate-900">Leadership</h2>
          <p className="mt-2 text-dim text-sm max-w-2xl">The Kenche family — three generations of engineering leadership driving Accurate forward.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {LEADERSHIP.map((m, i) => (
            <Reveal key={m.name} delay={i * 0.08} className="relative bg-white border border-[#0055FF]/30 rounded-2xl p-6 card-hover" data-testid={`leader-card-${i}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-xl text-slate-900 leading-tight">{m.name}</h3>
                  <p className="text-sm text-[#0055FF] mt-1">{m.role}</p>
                </div>
                <a href={m.linkedin} target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 shrink-0 rounded-full border border-slate-200 flex items-center justify-center text-slate-700 hover:text-white hover:bg-[#0055FF] hover:border-[#0055FF] transition-colors"
                  data-testid={`linkedin-${m.name}`} aria-label={`${m.name} on LinkedIn`}>
                  <Linkedin className="w-4 h-4" />
                </a>
              </div>
              <p className="mt-4 text-dim text-sm leading-relaxed">{m.bio}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* Infrastructure highlights (compact) */}
    <section className="py-8 md:py-12 border-t border-slate-200">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-4">
        {[
          [Gauge, "Reconditioning Expertise", "Full mechanical rebuild and CNC integration under one roof."],
          [Award, "Quality Systems", "ISO-certified processes with laser & ballbar calibration."],
          [Target, "Testing & Acceptance", "Every machine validated against documented acceptance criteria."],
        ].map(([Icon, t, d]) => (
          <Reveal key={t} className="bg-white border border-[#0055FF]/30 rounded-2xl p-6 card-hover">
            <div className="w-10 h-10 rounded-lg bg-[#0055FF]/10 border border-[#0055FF]/25 flex items-center justify-center"><Icon className="w-5 h-5 text-[#0055FF]" /></div>
            <h3 className="mt-4 text-base text-slate-900">{t}</h3>
            <p className="mt-2 text-dim text-sm">{d}</p>
          </Reveal>
        ))}
      </div>
    </section>

    {/* Certifications merged in — no separate page tab */}
    <section className="py-8 md:py-12 border-t border-slate-200 bg-white" data-testid="certifications-section">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="mb-8">
          <span className="eyebrow">Quality &amp; Compliance</span>
          <h2 className="mt-3 text-2xl md:text-3xl text-slate-900">Certified to the highest standards</h2>
        </div>

        {/* Primary ISO card */}
        <Reveal className="relative bg-white border border-[#0055FF]/30 rounded-2xl p-6 md:p-8 overflow-hidden card-hover" data-testid="primary-cert">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full" style={{ background: "radial-gradient(circle, rgba(0,85,255,0.10), transparent 70%)" }} />
          <div className="relative grid md:grid-cols-[auto_1fr_auto] gap-6 items-center">
            <div className="w-16 h-16 rounded-xl bg-[#0055FF]/10 border border-[#0055FF]/30 flex items-center justify-center">
              <ShieldCheck className="w-8 h-8 text-[#0055FF]" />
            </div>
            <div>
              <span className="text-xs uppercase tracking-widest text-[#0055FF] font-semibold">Primary Certification</span>
              <h3 className="mt-1 font-display text-2xl md:text-3xl leading-tight text-slate-900">{PRIMARY_CERTIFICATION.name}</h3>
              <p className="mt-1 text-sm text-[#0055FF]">{PRIMARY_CERTIFICATION.subtitle}</p>
              <p className="mt-3 text-dim text-sm leading-relaxed max-w-2xl">{PRIMARY_CERTIFICATION.desc}</p>
            </div>
          </div>
        </Reveal>

        {/* Udyam Registration — same layout as ISO */}
        <Reveal delay={0.05} className="relative bg-white border border-[#0055FF]/30 rounded-2xl p-6 md:p-8 overflow-hidden card-hover mt-4" data-testid="udyam-cert">
          <div className="absolute -top-16 -left-16 w-64 h-64 rounded-full" style={{ background: "radial-gradient(circle, rgba(0,85,255,0.08), transparent 70%)" }} />
          <div className="relative grid md:grid-cols-[auto_1fr_auto] gap-6 items-center">
            <div className="w-16 h-16 rounded-xl bg-[#0055FF]/10 border border-[#0055FF]/30 flex items-center justify-center">
              <BadgeCheck className="w-8 h-8 text-[#0055FF]" />
            </div>
            <div>
              <span className="text-xs uppercase tracking-widest text-[#0055FF] font-semibold">Government Registration</span>
              <h3 className="mt-1 font-display text-2xl md:text-3xl leading-tight text-slate-900">{UDYAM_REGISTRATION.name}</h3>
              <p className="mt-1 text-sm text-[#0055FF]">{UDYAM_REGISTRATION.subtitle}</p>
              <p className="mt-3 text-dim text-sm leading-relaxed max-w-2xl">{UDYAM_REGISTRATION.desc}</p>
            </div>
            <div className="shrink-0 text-right">
              <p className="text-[10px] uppercase tracking-widest text-slate-500">Registration No.</p>
              <p className="mt-1 font-display text-sm md:text-base text-slate-900 font-bold" data-testid="udyam-number">{UDYAM_REGISTRATION.regNo}</p>
            </div>
          </div>
        </Reveal>

        {/* CNC control partners (not "certified") */}
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <Reveal className="bg-white border border-[#0055FF]/30 rounded-2xl p-5 md:p-6 card-hover flex items-start gap-4" data-testid="control-partner-fanuc">
            <div className="w-16 h-16 shrink-0 rounded-xl bg-[#111] flex items-center justify-center">
              <span className="font-display text-lg font-black tracking-tight" style={{ color: "#FFCC00" }}>FANUC</span>
            </div>
            <div>
              <h3 className="text-base text-slate-900">FANUC CNC Controllers</h3>
              <p className="mt-1 text-dim text-sm leading-relaxed">We use FANUC CNC controllers on our machines, with assistance and support provided directly from the FANUC support team.</p>
              <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#0055FF] font-medium"><BadgeCheck className="w-3.5 h-3.5" /> Direct OEM Support</div>
            </div>
          </Reveal>
          <Reveal delay={0.05} className="bg-white border border-[#0055FF]/30 rounded-2xl p-5 md:p-6 card-hover flex items-start gap-4" data-testid="control-partner-siemens">
            <div className="w-16 h-16 shrink-0 rounded-xl bg-white border border-slate-200 flex items-center justify-center">
              <span className="font-display text-base font-bold tracking-tight" style={{ color: "#009999" }}>SIEMENS</span>
            </div>
            <div>
              <h3 className="text-base text-slate-900">Siemens SINUMERIK Controllers</h3>
              <p className="mt-1 text-dim text-sm leading-relaxed">We use Siemens SINUMERIK CNC controllers on our machines, with assistance and support provided directly from the Siemens support team.</p>
              <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#0055FF] font-medium"><BadgeCheck className="w-3.5 h-3.5" /> Direct OEM Support</div>
            </div>
          </Reveal>
        </div>

        {/* Accreditations */}
        <div className="mt-8">
          <p className="text-xs uppercase tracking-widest text-slate-500 mb-4">Accreditation &amp; Recognition</p>
          <div className="grid md:grid-cols-3 gap-4">
            {ACCREDITATIONS.map((a, i) => (
              <Reveal key={a.name} delay={i * 0.05} className="border border-[#0055FF]/30 bg-white rounded-2xl p-5 text-center card-hover" data-testid={`accred-${i}`}>
                <div className="w-12 h-12 mx-auto rounded-full bg-[#0055FF]/10 border border-[#0055FF]/30 flex items-center justify-center">
                  <Award className="w-6 h-6 text-[#0055FF]" />
                </div>
                <h4 className="mt-3 font-display text-lg text-slate-900">{a.name}</h4>
                <p className="mt-1 text-xs text-dim leading-relaxed">{a.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="py-10 md:py-14 text-center border-t border-slate-200 bg-slate-50">
      <Reveal>
        <h2 className="text-2xl md:text-3xl text-slate-900">Ready to work with us?</h2>
        <div className="mt-5 flex flex-wrap gap-3 justify-center">
          <Link to="/contact" className="btn-primary !text-sm">Get in touch <ArrowRight className="w-4 h-4" /></Link>
          <Link to="/enquiry" className="btn-ghost !text-sm">Send an Enquiry</Link>
        </div>
      </Reveal>
    </section>
  </div>
);

export default About;
